import React, { useEffect, useState } from "react";
import {
  Wallet,
  Gift,
  CreditCard as StripeIcon,
  CircleDollarSign,
  DollarSign,
  Banknote,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import SquarePayment from "./SquarePayment";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const PaymentSection: React.FC<{
  handleOrder: (
    paymentMethod: string,
    userId?: string,
    transactionId?: string,
  ) => void;
  deliverable: boolean;
  pricing: number;
  bonusAdjustedPricing?: number;
  onPaymentMethodChange?: (method: string) => void;
  hotelId?: string;
  cashEnabled?: boolean;
  validateBeforePayment?: () => boolean;
}> = ({
  handleOrder,
  pricing,
  deliverable,
  onPaymentMethodChange,
  bonusAdjustedPricing = 0,
  hotelId,
  cashEnabled,
  validateBeforePayment,
}) => {
  const { user, isAuth, currencySymbol, squareApplicationId } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  // Anything under a cent is float noise, not a real card remainder
  const hasBonusCardRemainder = bonusAdjustedPricing >= 0.01;

  const blackListForCash = user?.blackListForCash ?? false;
  const blackListForCard = user?.blackListForCard ?? false;
  // If user is blacklisted for card, treat squareApplicationId as absent
  const effectiveSquareId = blackListForCard ? null : squareApplicationId;

  // Temporary wallet balances (will be replaced with actual values later)
  const [walletBalance, setWalletBalance] = useState(
    user?.wallet?.balance || 0,
  );
  const [bonusWalletBalance, setBonusWalletBalance] = useState(
    user?.bonusWallet?.balance || 0,
  );

  const paymentMethods = [
    ...(effectiveSquareId
      ? [
          {
            id: "square",
            name: "Card Payment (Square)",
            icon: <StripeIcon className="w-5 h-5" />,
            balance: null,
            description: "Pay with Credit/Debit Card",
          },
        ]
      : []),
    ...(!blackListForCash && cashEnabled
      ? [
          {
            id: "cash",
            name: "Cash",
            icon: <Banknote className="w-5 h-5" />,
            balance: null,
            description: "Pay via Cash",
          },
        ]
      : []),
    ...(user && isAuth
      ? [
          {
            id: "wallet",
            name: "Wallet",
            icon: <Wallet className="w-5 h-5" />,
            balance: walletBalance,
            description: `Balance: ${currencySymbol}${walletBalance.toFixed(2)}`,
          },
          {
            id: "bonus",
            name:
              hasBonusCardRemainder && effectiveSquareId
                ? "Cash Earned + Card Payment (Square)"
                : "Cash Earned",
            icon: <Gift className="w-5 h-5" />,
            balance: bonusWalletBalance,
            description:
              hasBonusCardRemainder && effectiveSquareId
                ? `Balance: ${currencySymbol}${bonusWalletBalance.toFixed(2)} + Credit/Debit Card`
                : `Balance: ${currencySymbol}${bonusWalletBalance.toFixed(2)}`,
          },
          ...(bonusWalletBalance > 0 && walletBalance > 0
            ? [
                {
                  id: "bonus_wallet",
                  name: "Bonus + Wallet",
                  icon: <CircleDollarSign className="w-5 h-5" />,
                  balance: bonusWalletBalance + walletBalance,
                  description: `Bonus: ${currencySymbol}${bonusWalletBalance.toFixed(2)} + Wallet: ${currencySymbol}${walletBalance.toFixed(2)}`,
                },
              ]
            : []),
        ]
      : []),
  ];

  useEffect(() => {
    setWalletBalance(user?.wallet?.balance || 0);
    setBonusWalletBalance(user?.bonusWallet?.balance || 0);
  }, [user]);

  const handlePaymentMethodClick = (methodId: string) => {
    setSelectedMethod(methodId);
    onPaymentMethodChange?.(methodId);
    if (
      methodId === "square" ||
      (methodId === "bonus" && hasBonusCardRemainder && effectiveSquareId)
    ) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    // Open card gateway in modal for square flow and bonus remaining flow
    if (
      selectedMethod === "square" ||
      (selectedMethod === "bonus" && hasBonusCardRemainder && effectiveSquareId)
    ) {
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
  }, [selectedMethod, hasBonusCardRemainder, effectiveSquareId]);

  const handleProceedToPay = () => {
    if (selectedMethod === "cash" || selectedMethod === "wallet" || selectedMethod === "bonus_wallet") {
      setIsProcessing(true);
      try {
        const result = handleOrder(selectedMethod) as any;
        if (result && typeof result.then === "function") {
          result
            .then(() => {
              setTimeout(() => setIsProcessing(false), 9000);
            })
            .catch((error: any) => {
              console.error("Payment failed:", error);
              setIsProcessing(false);
            });
        } else {
          setTimeout(() => setIsProcessing(false), 9000);
        }
      } catch (error) {
        console.error("Payment failed:", error);
        setIsProcessing(false);
      }
    } else if (selectedMethod === "bonus") {
      if (hasBonusCardRemainder) {
        // Gateway is shown automatically for this flow
        return;
      } else {
        // Bonus fully covers everything
        setIsProcessing(true);
        try {
          const result = handleOrder("bonus") as any;
          if (result && typeof result.then === "function") {
            result
              .then(() => {
                setTimeout(() => setIsProcessing(false), 9000);
              })
              .catch((error: any) => {
                console.error("Payment failed:", error);
                setIsProcessing(false);
              });
          } else {
            setTimeout(() => setIsProcessing(false), 9000);
          }
        } catch (error) {
          console.error("Payment failed:", error);
          setIsProcessing(false);
        }
      }
    }
  };

  const isMethodDisabled = (method: (typeof paymentMethods)[0]) => {
    if (method.balance === null) return false;
    if (method.id === "bonus") return (method.balance ?? 0) <= 0;
    // bonus_wallet: wallet must cover the remaining after bonus (bonusAdjustedPricing)
    if (method.id === "bonus_wallet")
      return walletBalance < bonusAdjustedPricing;
    return method.balance < pricing;
  };

  return (
    <div>
      <div
        className={`bg-white rounded-lg border-[1px] border-black/10 p-6 mb-6 dm-payment-section ${!deliverable ? "opacity-50 bg-black-500 cursor-not-allowed " : ""}`}
      >
        <div className="w-full bg-white py-4 px-4  border-[1px] border-black/10 rounded-[15px] rounded-bl-none rounded-br-none overflow-hidden">
          <div className="flex items-center justify-between ">
            <div className="flex items-center gap-4">
              <div
                className={` p-2 bg-transparent w-[45px] h-[45px] border-[1px] border-black/20 rounded-full flex items-center justify-center`}
              >
                <DollarSign className="w- h-5" />
              </div>
              <h3 className="font-semibold text-[20px] text-main m-0">
                Payment{" "}
              </h3>
            </div>
          </div>
        </div>
        {deliverable && (
          <div className="space-y-4">
            {/* Payment Methods */}
            <div className="space-y-3">
              {paymentMethods.map((method) => {
                const disabled = isMethodDisabled(method);
                return (
                  <button
                    key={method.id}
                    disabled={disabled || isProcessing}
                    onClick={() => handlePaymentMethodClick(method.id)}
                    className={`dm-pay-method w-full min-h-[108px] p-4 border-[1px] rounded-lg rounded-tl-none rounded-tr-none text-left transition-all ${
                      selectedMethod === method.id
                        ? "border-main bg-white dm-payment-method-selected"
                        : "border-black/10 hover:border-black/20"
                    } ${
                      disabled || isProcessing
                        ? "opacity-50 cursor-not-allowed bg-gray-50"
                        : "hover:bg-black/5"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={` p-2 bg-transparent gap-0 w-[45px] h-[45px] border-[1px] border-black/20 rounded-full flex items-center justify-center ${
                            selectedMethod === method.id
                              ? "text-main"
                              : "text-gray-600"
                          }`}
                        >
                          {method.icon}
                        </div>
                        <div className="flex flex-col justify-center min-h-[60px]">
                          <p
                            className={`font-medium mb-0 ${
                              selectedMethod === method.id
                                ? "text-main"
                                : "text-gray-700"
                            }`}
                          >
                            {method.name}
                          </p>
                          <p className="text-sm mb-0 text-gray-500">
                            {method.description}
                          </p>
                          <p
                            className={`text-xs m-0 mt-1 ${
                              disabled ? "text-main visible" : "invisible"
                            }`}
                          >
                            Insufficient balance
                          </p>
                        </div>
                      </div>
                      <div
                        className={`dm-pay-selected w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedMethod === method.id
                            ? "border-black/30 bg-main"
                            : "border-gray-300"
                        }`}
                      >
                        {selectedMethod === method.id && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-center">
              {/* Proceed to Pay Button */}
              {selectedMethod &&
                selectedMethod !== "square" &&
                !(selectedMethod === "bonus" && hasBonusCardRemainder && effectiveSquareId) && (
                <>
                  <button
                    className="dm-pay-cta w-full bg-main text-white font-semibold p-3 rounded-md   disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={handleProceedToPay}
                    disabled={
                      isProcessing ||
                      !selectedMethod ||
                      (selectedMethod === "wallet" &&
                        walletBalance < pricing) ||
                      (selectedMethod === "bonus" && bonusWalletBalance <= 0) ||
                      (selectedMethod === "bonus_wallet" &&
                        walletBalance < bonusAdjustedPricing)
                    }
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      `PROCEED TO PAY `
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent
            className="max-h-[90vh] overflow-y-auto sm:max-w-[720px] bg-white p-4 sm:p-5 dm-payment-modal"
            showCloseButton
          >
            <DialogTitle className="text-[18px] font-semibold text-main">
              Card Payment
            </DialogTitle>
            <SquarePayment
              pricing={selectedMethod === "bonus" && effectiveSquareId ? bonusAdjustedPricing : pricing}
              handleOrder={
                selectedMethod === "bonus" && effectiveSquareId
                  ? (_, userId, transactionId) =>
                      // bonus_square: backend deducts the bonus portion and records both payments
                      handleOrder("bonus_square", userId, transactionId)
                  : handleOrder
              }
              onSuccess={() => {
                setIsOpen(false);
                setSelectedMethod("");
              }}
              hotelId={hotelId}
              validateBeforePayment={validateBeforePayment}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PaymentSection;
